import { useCallback, useEffect, useMemo, useState } from 'react'
import type { AudioNote } from '@highlight/common'
import { useAudioNotes } from '@renderer/hooks/audio-notes'
import { useFooterSlotClaim } from '../../../atoms'
import { isMeetingTourUnseen, useMeetingTourStatus } from './meeting-tour-status'
import type { MeetingViewModel } from '../../../views/meeting-view/use-meeting-view-model'

/** Let the meeting layout settle (sections stagger in, the floating bar swaps
 *  to its past controls) before offering a tour of it. */
const PROMPT_SETTLE_DELAY_MS = 1500

/**
 * Decides when to offer the meeting tour: on a finished meeting, for a user
 * whose only meeting it is. Never during a recording.
 *
 * The offer stands until it is answered. It is not tied to the moment a
 * recording ends, and leaving the meeting does not retire it — a user who
 * navigates away mid-offer gets it again on their next finished meeting, and
 * keeps getting it until they take the tour or turn it down. Only `No thanks`,
 * Escape, and completing the tour write a terminal status.
 *
 * The offer also waits for somewhere to be seen. A footer-slot claim (the
 * Notepad) leaves the composer, and the `before` slot the card renders in,
 * laid out but invisible, so the offer holds until the slot is free rather
 * than showing to no one.
 */
export function useMeetingTourPrompt(model: MeetingViewModel) {
  const [status, setStatus] = useMeetingTourStatus()
  const [promptVisible, setPromptVisible] = useState(false)

  // See the doc above: a claim hides the slot the card renders in.
  const composerCovered = useFooterSlotClaim() !== null

  const isFinishedMeeting = model.variant === 'past' && !!model.audioNoteId

  // The saved-notes list is only consulted while an offer is actually possible;
  // once the status burns the query never runs again.
  const shouldEvaluate = isMeetingTourUnseen(status) && isFinishedMeeting
  // Arrives with the status in one write, so it can never be read stale.
  const forceFirstMeeting = status === 'replay'
  // The dev override answers the same question the list does, so don't ask.
  const notes = useAudioNotes({ enabled: shouldEvaluate && !forceFirstMeeting })

  // Any own note besides the one on screen means this isn't a first meeting.
  // Shared notes don't count. Someone else's meeting landing in the list
  // shouldn't cost a new user their tour.
  const hasPriorMeetings = useMemo(
    () =>
      notes.data.some((note) => {
        const shared = (note as AudioNote & { isShared?: boolean }).isShared
        return !shared && note.id !== model.audioNoteId
      }),
    [notes.data, model.audioNoteId],
  )

  useEffect(() => {
    if (!shouldEvaluate) return
    if (!forceFirstMeeting) {
      if (notes.isLoading) return
      if (hasPriorMeetings) {
        // An existing user burns the status without ever showing UI. The tour
        // is a first-meeting offer, not a recurring one.
        setStatus('dismissed')
        return
      }
    }
    if (composerCovered) return
    const id = setTimeout(() => setPromptVisible(true), PROMPT_SETTLE_DELAY_MS)
    return () => clearTimeout(id)
  }, [shouldEvaluate, forceFirstMeeting, notes.isLoading, hasPriorMeetings, composerCovered, setStatus])

  const declinePrompt = useCallback(() => {
    setPromptVisible(false)
    setStatus('dismissed')
  }, [setStatus])

  /** Accepting the offer: the tour takes over; status is written when it ends. */
  const hidePrompt = useCallback(() => setPromptVisible(false), [])

  // Opening the notepad over a standing offer withdraws it until the composer
  // returns. While withdrawn, Escape belongs to the notepad and must not
  // decline a card nobody can see.
  const offerShowing = promptVisible && !composerCovered

  useEffect(() => {
    if (!offerShowing) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') declinePrompt()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [offerShowing, declinePrompt])

  return { promptVisible: offerShowing, declinePrompt, hidePrompt }
}
